import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, GitBranch, AlertCircle, Loader2, Activity } from 'lucide-react'
import { workflowsApi } from '@/lib/api'
import type { WorkflowDefinition } from '@/types'

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    workflowsApi.list()
      .then(res => setWorkflows(res.data))
      .catch(() => setError('Erro ao carregar workflows'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerir fluxos de aprovação e automações
          </p>
        </div>
        <Link to="/workflows/new" className="btn btn-primary">
          <Plus className="mr-2 h-4 w-4" />
          Novo Workflow
        </Link>
      </div>

      {loading && (
        <div className="mt-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      )}

      {error && (
        <div className="mt-6 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && workflows.length === 0 && (
        <div className="mt-12 text-center">
          <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Nenhum workflow criado</h3>
          <p className="mt-2 text-sm text-gray-500">
            Crie o primeiro workflow para automatizar processos de aprovação.
          </p>
          <Link to="/workflows/new" className="btn btn-primary mt-6">
            <Plus className="mr-2 h-4 w-4" />
            Criar Workflow
          </Link>
        </div>
      )}

      {!loading && !error && workflows.length > 0 && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {workflows.map(wf => (
            <Link
              key={wf.id}
              to={`/workflows/${wf.id}/edit`}
              className="card group hover:border-primary-300 hover:shadow-md transition"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <GitBranch className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{wf.name}</h3>
                  {wf.description && (
                    <p className="mt-0.5 text-xs text-gray-500 truncate">{wf.description}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-600">
                    {wf.states_count} estados · {wf.transitions_count} transições
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    {wf.is_active ? (
                      <span className="badge badge-success">Ativo</span>
                    ) : (
                      <span className="badge badge-error">Inativo</span>
                    )}
                    {wf.category && (
                      <span className="badge badge-info">{wf.category}</span>
                    )}
                    {wf.active_instances_count > 0 && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Activity className="h-3 w-3" />
                        {wf.active_instances_count} ativas
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

